import React, { useState } from "react";
import { supabase } from "./supabaseClient";

const WEIGHT_CLASSES = [
  "Strawweight (up to 105 lbs)",
  "Minimumweight (up to 108 lbs)",
  "Light Flyweight (up to 108 lbs)",
  "Flyweight (up to 112 lbs)",
  "Super Flyweight (up to 115 lbs)",
  "Bantamweight (up to 118 lbs)",
  "Super Bantamweight (up to 122 lbs)",
  "Featherweight (up to 126 lbs)",
  "Super Featherweight (up to 130 lbs)",
  "Lightweight (up to 135 lbs)",
  "Super Lightweight (up to 140 lbs)",
  "Welterweight (up to 147 lbs)",
  "Super Welterweight (up to 154 lbs)",
  "Middleweight (up to 160 lbs)",
  "Super Middleweight (up to 168 lbs)",
  "Light Heavyweight (up to 175 lbs)",
  "Cruiserweight (up to 200 lbs)",
  "Heavyweight (over 200 lbs)"
];

const EXPERIENCE_LEVELS = [
  "Beginner (0-1 years)",
  "Amateur (1-3 years)",
  "Intermediate (3-5 years)",
  "Advanced (5-10 years)",
  "Professional (10+ years)"
];

const BOXING_STYLES = [
  { value: "orthodox", label: "Orthodox" },
  { value: "southpaw", label: "Southpaw" },
  { value: "philly_shell", label: "Philly Shell" },
  { value: "peek_a_boo", label: "Peek-a-Boo" },
  { value: "soviet", label: "Soviet/Kronk" },
  { value: "swarmer", label: "Swarmer" }
];

const inputStyle = {
  width:"100%",
  padding:"14px",
  backgroundColor:"rgba(255,255,255,0.04)",
  color:"white",
  border:"1px solid rgba(255,255,255,0.08)",
  borderRadius:"12px",
  fontSize:"0.95rem",
  fontFamily:"Inter, sans-serif",
  boxSizing:"border-box",
  outline:"none",
  marginBottom:"12px"
};

const selectStyle = {
  ...inputStyle,
  cursor:"pointer"
};

function Auth({ onLogin, onBack }) {
  const [step, setStep] = useState(1);
  const [isSignUp, setIsSignUp] = useState(false);
  const [role, setRole] = useState("fighter");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [age, setAge] = useState("");
  const [weightClass, setWeightClass] = useState("");
  const [experience, setExperience] = useState("");
  const [boxingStyle, setBoxingStyle] = useState("orthodox");
  const [gymName, setGymName] = useState("");
  const [coachingStyle, setCoachingStyle] = useState("");

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }
    onLogin(data.user, data.user.user_metadata.role);
    setLoading(false);
  };

  const handleSignUp = async () => {
    setLoading(true);
    setError(null);
    const metadata = role === "fighter"
      ? { role, full_name: fullName, age: parseInt(age), weight_class: weightClass, experience, boxing_style: boxingStyle }
      : { role, full_name: fullName, gym_name: gymName, coaching_style: coachingStyle };

    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: metadata }
    });
    if (error) { setError(error.message); setLoading(false); return; }

    await supabase.from("profiles").upsert({
      id: data.user.id,
      email,
      role,
      full_name: fullName,
      age: role === "fighter" ? parseInt(age) : null,
      weight_class: role === "fighter" ? weightClass : null,
      experience: role === "fighter" ? experience : null,
      boxing_style: role === "fighter" ? boxingStyle : null,
      gym_name: role === "coach" ? gymName : null,
      coaching_style: role === "coach" ? coachingStyle : null
    });

    onLogin(data.user, role);
    setLoading(false);
  };

  const containerStyle = {
    minHeight:"100vh",
    backgroundColor:"#080810",
    display:"flex",
    flexDirection:"column",
    alignItems:"center",
    justifyContent:"center",
    fontFamily:"Inter, sans-serif",
    color:"white",
    padding:"20px",
    position:"relative",
    overflow:"hidden"
  };

  const cardStyle = {
    backgroundColor:"rgba(255,255,255,0.03)",
    backdropFilter:"blur(30px)",
    padding:"40px",
    borderRadius:"24px",
    width:"100%",
    maxWidth:"440px",
    border:"1px solid rgba(255,255,255,0.07)",
    boxShadow:"0 30px 60px rgba(0,0,0,0.6)"
  };

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0,0) scale(1); }
          33% { transform: translate(30px,-50px) scale(1.05); }
          66% { transform: translate(-20px,20px) scale(0.95); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0,0) scale(1); }
          33% { transform: translate(-40px,30px) scale(1.08); }
          66% { transform: translate(20px,-40px) scale(0.92); }
        }
        input::placeholder, select option { color: #444; background: #080810; }
        select { appearance: none; }
      `}</style>

      <div style={{position:"absolute",inset:0,overflow:"hidden",zIndex:0,pointerEvents:"none"}}>
        <div style={{position:"absolute",top:"-20%",left:"-10%",width:"600px",height:"600px",borderRadius:"50%",background:"radial-gradient(circle, rgba(233,69,96,0.15) 0%, transparent 70%)",animation:"float1 12s ease-in-out infinite"}}/>
        <div style={{position:"absolute",bottom:"-20%",right:"-10%",width:"700px",height:"700px",borderRadius:"50%",background:"radial-gradient(circle, rgba(255,107,53,0.12) 0%, transparent 70%)",animation:"float2 15s ease-in-out infinite"}}/>
      </div>

      <div style={{position:"relative",zIndex:1,width:"100%",maxWidth:"440px"}}>
        <div style={{marginBottom:"35px",textAlign:"center"}}>
          <div style={{fontSize:"3rem",marginBottom:"10px"}}>🥊</div>
          <h1 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"4rem",margin:0,background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",letterSpacing:"4px",lineHeight:1}}>FIGHTCOACH AI</h1>
          <p style={{color:"#444",margin:"10px 0 0 0",fontSize:"0.8rem",letterSpacing:"3px",textTransform:"uppercase"}}>Train Smarter. Fight Better.</p>
        </div>

        <div style={cardStyle}>
          {!isSignUp ? (
            <>
              <h2 style={{textAlign:"center",fontSize:"1.3rem",marginBottom:"25px",fontWeight:"600"}}>Welcome Back</h2>
              <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
              <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
              {error && <p style={{color:"#e94560",fontSize:"0.88rem",marginBottom:"12px"}}>{error}</p>}
              <button onClick={handleLogin} disabled={loading} style={{width:"100%",padding:"15px",background: loading ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: loading ? "#444" : "white",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"700",fontFamily:"Inter, sans-serif",cursor: loading ? "not-allowed" : "pointer",letterSpacing:"1px",textTransform:"uppercase",boxShadow: loading ? "none" : "0 8px 25px rgba(233,69,96,0.35)"}}>
                {loading ? "Logging in..." : "Log In"}
              </button>
              <p style={{textAlign:"center",color:"#444",fontSize:"0.88rem",marginTop:"20px",marginBottom:0}}>
                Don't have an account? <span onClick={() => { setIsSignUp(true); setStep(1); }} style={{color:"#e94560",cursor:"pointer",fontWeight:"600"}}>Sign Up</span>
              </p>
            </>
          ) : (
            <>
              {step === 1 && (
                <>
                  <h2 style={{textAlign:"center",fontSize:"1.3rem",marginBottom:"8px",fontWeight:"600"}}>Create Account</h2>
                  <p style={{textAlign:"center",color:"#555",fontSize:"0.85rem",marginBottom:"20px"}}>Step 1 of 2 — Basic Info</p>
                  <div style={{display:"flex",gap:"10px",marginBottom:"16px"}}>
                    <button onClick={() => setRole("fighter")} style={{flex:1,padding:"14px",background: role==="fighter" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.04)",color:"white",border: role==="fighter" ? "none" : "1px solid rgba(255,255,255,0.08)",borderRadius:"12px",cursor:"pointer",fontSize:"0.95rem",fontWeight:"600",fontFamily:"Inter, sans-serif"}}>🥋 Fighter</button>
                    <button onClick={() => setRole("coach")} style={{flex:1,padding:"14px",background: role==="coach" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.04)",color:"white",border: role==="coach" ? "none" : "1px solid rgba(255,255,255,0.08)",borderRadius:"12px",cursor:"pointer",fontSize:"0.95rem",fontWeight:"600",fontFamily:"Inter, sans-serif"}}>🎯 Coach</button>
                  </div>
                  <input type="text" placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} />
                  <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
                  <input type="password" placeholder="Password (min 6 characters)" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
                  {error && <p style={{color:"#e94560",fontSize:"0.88rem",marginBottom:"12px"}}>{error}</p>}
                  <button onClick={() => {
                    if (!fullName || !email || !password) { setError("Please fill in all fields."); return; }
                    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
                    setError(null);
                    setStep(2);
                  }} style={{width:"100%",padding:"15px",background:"linear-gradient(135deg, #e94560, #ff6b35)",color:"white",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"700",fontFamily:"Inter, sans-serif",cursor:"pointer",letterSpacing:"1px",textTransform:"uppercase",boxShadow:"0 8px 25px rgba(233,69,96,0.35)"}}>
                    Continue →
                  </button>
                  <p style={{textAlign:"center",color:"#444",fontSize:"0.88rem",marginTop:"20px",marginBottom:0}}>
                    Already have an account? <span onClick={() => setIsSignUp(false)} style={{color:"#e94560",cursor:"pointer",fontWeight:"600"}}>Log In</span></p><p style={{textAlign:"center",color:"#333",fontSize:"0.85rem",marginTop:"10px",marginBottom:0}}><span onClick={onBack} style={{color:"#444",cursor:"pointer"}}>← Back to home</span>
                  </p>
                </>
              )}

              {step === 2 && role === "fighter" && (
                <>
                  <h2 style={{textAlign:"center",fontSize:"1.3rem",marginBottom:"8px",fontWeight:"600"}}>Fighter Profile</h2>
                  <p style={{textAlign:"center",color:"#555",fontSize:"0.85rem",marginBottom:"20px"}}>Step 2 of 2 — Your Details</p>
                  <input type="number" placeholder="Age" value={age} onChange={e => setAge(e.target.value)} style={inputStyle} />
                  <select value={weightClass} onChange={e => setWeightClass(e.target.value)} style={selectStyle}>
                    <option value="">Select weight class</option>
                    {WEIGHT_CLASSES.map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                  <select value={experience} onChange={e => setExperience(e.target.value)} style={selectStyle}>
                    <option value="">Select experience level</option>
                    {EXPERIENCE_LEVELS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <label style={{display:"block",marginBottom:"10px",color:"#888",fontSize:"0.8rem",textTransform:"uppercase",letterSpacing:"1px"}}>Primary Style</label>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"8px",marginBottom:"16px"}}>
                    {BOXING_STYLES.map(s => (
                      <div key={s.value} onClick={() => setBoxingStyle(s.value)} style={{padding:"10px",borderRadius:"10px",cursor:"pointer",textAlign:"center",border: boxingStyle===s.value ? "1px solid rgba(233,69,96,0.6)" : "1px solid rgba(255,255,255,0.06)",background: boxingStyle===s.value ? "rgba(233,69,96,0.15)" : "rgba(255,255,255,0.02)",color: boxingStyle===s.value ? "#e94560" : "#888",fontSize:"0.85rem",fontWeight:"600"}}>
                        {s.label}
                      </div>
                    ))}
                  </div>
                  {error && <p style={{color:"#e94560",fontSize:"0.88rem",marginBottom:"12px"}}>{error}</p>}
                  <button onClick={handleSignUp} disabled={loading} style={{width:"100%",padding:"15px",background: loading ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: loading ? "#444" : "white",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"700",fontFamily:"Inter, sans-serif",cursor: loading ? "not-allowed" : "pointer",letterSpacing:"1px",textTransform:"uppercase",boxShadow: loading ? "none" : "0 8px 25px rgba(233,69,96,0.35)"}}>
                    {loading ? "Creating account..." : "Create Account"}
                  </button>
                  <button onClick={() => setStep(1)} style={{width:"100%",padding:"12px",background:"transparent",color:"#555",border:"none",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",marginTop:"10px"}}>← Back</button>
                </>
              )}

              {step === 2 && role === "coach" && (
                <>
                  <h2 style={{textAlign:"center",fontSize:"1.3rem",marginBottom:"8px",fontWeight:"600"}}>Coach Profile</h2>
                  <p style={{textAlign:"center",color:"#555",fontSize:"0.85rem",marginBottom:"20px"}}>Step 2 of 2 — Your Details</p>
                  <input type="text" placeholder="Gym name" value={gymName} onChange={e => setGymName(e.target.value)} style={inputStyle} />
                  <input type="text" placeholder="Your coaching philosophy / style" value={coachingStyle} onChange={e => setCoachingStyle(e.target.value)} style={inputStyle} />
                  {error && <p style={{color:"#e94560",fontSize:"0.88rem",marginBottom:"12px"}}>{error}</p>}
                  <button onClick={handleSignUp} disabled={loading} style={{width:"100%",padding:"15px",background: loading ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: loading ? "#444" : "white",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"700",fontFamily:"Inter, sans-serif",cursor: loading ? "not-allowed" : "pointer",letterSpacing:"1px",textTransform:"uppercase",boxShadow: loading ? "none" : "0 8px 25px rgba(233,69,96,0.35)"}}>
                    {loading ? "Creating account..." : "Create Account"}
                  </button>
                  <button onClick={() => setStep(1)} style={{width:"100%",padding:"12px",background:"transparent",color:"#555",border:"none",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",marginTop:"10px"}}>← Back</button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Auth;
